import { useCallback, useEffect, useRef } from "react";
import { RESEARCH_TREE } from "../data/research";
import { DEFAULT_STATS } from "../utils/save";

// ── Helpers ───────────────────────────────────────────────────────────────────

// Normalize req entries to { id, minLevel } shape
function normalizeReqs(req) {
  return (req ?? []).map(r => typeof r === "string" ? { id: r, minLevel: 1 } : r);
}

// Returns the level entry for a given research at a given 1-indexed level
export function getResearchLevel(entry, level) {
  return entry.levels[level - 1] ?? null;
}

// Returns current purchased level for a given research id
export function currentLevel(researchLevels, id) {
  return researchLevels[id] ?? 0;
}

// Returns max purchasable level for an entry
export function maxLevel(entry) {
  return entry.levels.length;
}

// Check prereqs: all required entries must be at their minLevel.
// discoveredOutposts is optional; if the entry has minOutposts, it is enforced.
export function prereqsMet(researchLevels, entry, discoveredOutposts = Infinity) {
  const resOk = normalizeReqs(entry.req).every(r => (researchLevels[r.id] ?? 0) >= r.minLevel);
  const outpostOk = entry.minOutposts == null || discoveredOutposts >= entry.minOutposts;
  return resOk && outpostOk;
}

// ── Stat recompute ────────────────────────────────────────────────────────────
// Recomputes all stats from scratch given the current researchLevels map.
// Returns the new stats object.
export function computeStatsFromResearch(researchLevels) {
  const stats = { ...DEFAULT_STATS };

  // Track the highest "set" value seen per stat (for queue/slot upgrades)
  const setMax = {};

  for (const entry of RESEARCH_TREE) {
    const lvl = researchLevels[entry.id] ?? 0;
    if (lvl === 0) continue;

    for (let i = 0; i < lvl; i++) {
      const levelEntry = entry.levels[i];
      if (!levelEntry) continue;

      // Apply primary effect and optional extraEffect (used when a level tweaks two stats)
      for (const e of [levelEntry.effect, levelEntry.extraEffect].filter(Boolean)) {
        if (e.stat != null) {
          if (e.mult != null) {
            stats[e.stat] = (stats[e.stat] ?? 0) * (1 + e.mult);
          } else if (e.add != null) {
            stats[e.stat] = (stats[e.stat] ?? 0) + e.add;
          } else if (e.set != null) {
            setMax[e.stat] = Math.max(setMax[e.stat] ?? 0, e.set);
          }
        }
        if (e.visual === "plasma") {
          stats.plasmaVisual = true;
        }
        if (e.flag != null) {
          stats[e.flag] = true;
        }
      }
    }
  }

  // Apply set-max stats
  for (const [stat, val] of Object.entries(setMax)) {
    stats[stat] = val;
  }

  return stats;
}

// Returns all unlock strings that are active given current research levels
export function computeUnlocks(researchLevels) {
  const unlocks = new Set();
  for (const entry of RESEARCH_TREE) {
    const lvl = researchLevels[entry.id] ?? 0;
    for (let i = 0; i < lvl; i++) {
      const le = entry.levels[i];
      if (le?.effect?.unlock)      unlocks.add(le.effect.unlock);
      if (le?.extraEffect?.unlock) unlocks.add(le.extraEffect.unlock);
    }
  }
  return unlocks;
}

// ── Hook ──────────────────────────────────────────────────────────────────────
export function useResearch({
  researchLevels, setResearchLevels,
  inventory, setInventory,
  materials, setMaterials,
  setStats,
  discoveredTradeOutposts = 0,
  onFinalNodeMaxed,
}) {
  // Always-current refs so doResearch never reads stale closure values
  const researchLevelsRef       = useRef(researchLevels);
  const inventoryRef            = useRef(inventory);
  const materialsRef            = useRef(materials);
  const discoveredOutpostsRef   = useRef(discoveredTradeOutposts);
  useEffect(() => { researchLevelsRef.current     = researchLevels;       }, [researchLevels]);
  useEffect(() => { inventoryRef.current          = inventory;             }, [inventory]);
  useEffect(() => { materialsRef.current          = materials;             }, [materials]);
  useEffect(() => { discoveredOutpostsRef.current = discoveredTradeOutposts; }, [discoveredTradeOutposts]);

  // Purchase the next level of a research entry
  const doResearch = useCallback((resId) => {
    const researchLevels = researchLevelsRef.current;
    const inventory      = inventoryRef.current;
    const materials      = materialsRef.current;
    const outposts       = discoveredOutpostsRef.current;

    const entry = RESEARCH_TREE.find(r => r.id === resId);
    if (!entry) return;

    const curLvl = researchLevels[resId] ?? 0;
    const nextLvl = curLvl + 1;
    if (nextLvl > entry.levels.length) return;

    if (!prereqsMet(researchLevels, entry, outposts)) return;

    const levelEntry = entry.levels[curLvl];
    if (!levelEntry) return;

    const isRaw = levelEntry.costIsRaw ?? entry.costIsRaw ?? false;

    if (isRaw) {
      const inv = { ...inventory };
      for (const c of levelEntry.cost) { if ((inv[c.id] ?? 0) < c.qty) return; }
      for (const c of levelEntry.cost) { inv[c.id] -= c.qty; }
      setInventory(inv);
    } else {
      const mats = { ...materials };
      for (const c of levelEntry.cost) { if ((mats[c.id] ?? 0) < c.qty) return; }
      for (const c of levelEntry.cost) { mats[c.id] -= c.qty; }
      setMaterials(mats);
    }

    const newLevels = { ...researchLevels, [resId]: nextLvl };
    setResearchLevels(newLevels);
    setStats(computeStatsFromResearch(newLevels));

    // Notify when the final level of this node is purchased (for ◈1 award)
    if (nextLvl === entry.levels.length && onFinalNodeMaxed) {
      onFinalNodeMaxed(resId);
    }
  }, [setInventory, setMaterials, setResearchLevels, setStats, onFinalNodeMaxed]);

  return { doResearch, discoveredTradeOutposts };
}
